import { COURT_NAMES, displayTitle, type LegalCitation, type StudyTopic } from '@buakei/types';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  LoadingState,
  PreviewAction,
} from '@buakei/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, BookOpen, FileText, ListChecks, Lock, Sparkles } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import type { IdentityClient } from '@/auth/identity-client';
import { CaseBriefPanel } from '@/assistant/CaseBriefPanel';
import { CitationLink } from '@/inspector/CitationLink';
import {
  AreaFilter,
  caseHaystack,
  caseName,
  CaseTextResults,
  useCases,
} from '@/pages/library/CasePages';
import { useReaderTokens } from '@/pages/library/LibraryPages';
import { assistantApi } from '@/services/api';
import { demoContentService } from '@/services/content';
import { AssistantHome } from '@/shell/AssistantHome';
import { Page } from '@/shell/Page';
import { ResearchWorkbench } from '@/pages/professional/ResearchWorkbench';
import { QuizRunner } from './QuizRunner';

function ProgressBar({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-2 flex-1 overflow-hidden rounded-full bg-line-200"
      >
        <div className="h-full rounded-full bg-navy-900" style={{ width: `${value}%` }} />
      </div>
      <span className="w-10 text-right text-xs text-muted-600">{value}%</span>
    </div>
  );
}

function TopicCard({ topic }: { topic: StudyTopic }) {
  return (
    <Card>
      <CardHeader>
        <p className="text-sm text-muted-600">{topic.subject}</p>
        <CardTitle>{topic.title}</CardTitle>
        <CardDescription>{topic.summary}</CardDescription>
      </CardHeader>
      <CardContent>
        <ProgressBar value={topic.progress} label={`${topic.title} progress`} />
      </CardContent>
    </Card>
  );
}

function useTopics() {
  return useQuery({ queryKey: ['topics'], queryFn: demoContentService.listTopics });
}

/** Learning home (ADR-009): minimal and assistant-first. Topic progress lives in Subjects. */
export function LearningDashboard() {
  return (
    <AssistantHome
      title="Learning"
      question="What would you like to study today?"
      actions={[
        { to: '/learning/tutor', label: 'Ask Buakei', icon: Sparkles },
        { to: '/learning/subjects', label: 'Study a subject', icon: BookOpen },
        { to: '/learning/quizzes', label: 'Take a quiz', icon: ListChecks },
      ]}
      trustIcon={Lock}
      trust="Your study space is private to you, separate from any professional workspace."
    />
  );
}

/** The legal tutor: the same workbench lawyers use for research (owner request). */
export function TutorPage() {
  return <ResearchWorkbench learning />;
}

export function SubjectsPage() {
  const topics = useTopics();
  const filterId = useId();
  const [filter, setFilter] = useState('');
  const grouped = useMemo(() => {
    const term = filter.trim().toLowerCase();
    const list = (topics.data ?? []).filter((t) =>
      `${t.subject} ${t.title} ${t.summary}`.toLowerCase().includes(term),
    );
    const groups = new Map<string, StudyTopic[]>();
    for (const t of list) groups.set(t.subject, [...(groups.get(t.subject) ?? []), t]);
    return groups;
  }, [topics.data, filter]);

  return (
    <Page title="Subjects" description="Study topics grouped by subject. Not an official syllabus.">
      <div className="flex flex-col gap-1">
        <label htmlFor={filterId} className="text-sm font-medium text-navy-900">
          Filter topics
        </label>
        <input
          id={filterId}
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="h-10 max-w-sm rounded-md border border-line-200 bg-white px-3 text-ink-900"
        />
      </div>
      {topics.isPending ? <LoadingState label="Loading subjects" /> : null}
      {topics.isError ? (
        <ErrorState title="Subjects could not be loaded" onRetry={() => void topics.refetch()} />
      ) : null}
      {topics.data && grouped.size === 0 ? <EmptyState title="No matching topics" /> : null}
      {[...grouped].map(([subject, list]) => (
        <section
          key={subject}
          aria-labelledby={`subject-${subject}`}
          className="flex flex-col gap-3"
        >
          <h2 id={`subject-${subject}`} className="font-serif text-xl font-semibold text-navy-900">
            {subject}
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {list.map((t) => (
              <TopicCard key={t.id} topic={t} />
            ))}
          </div>
        </section>
      ))}
    </Page>
  );
}

const BRIEF_CITATIONS: Record<string, LegalCitation> = {
  p12: {
    id: 'brief_p12',
    sourceId: 'src_demo_alpha_v_beta',
    versionId: 'ver_demo_ab_v1',
    passageIds: ['psg_demo_ab_p12'],
    label: 'Alpha Example v Beta Illustration (DEMO), para. 12',
    dataOrigin: 'demo',
  },
  p15: {
    id: 'brief_p15',
    sourceId: 'src_demo_alpha_v_beta',
    versionId: 'ver_demo_ab_v1',
    passageIds: ['psg_demo_ab_p15'],
    label: 'Alpha Example v Beta Illustration (DEMO), para. 15',
    dataOrigin: 'demo',
  },
};

/** Case brief learning mode — a fictitious case, explicitly not editorially reviewed. */
/** DEMO brief for DEMO personas; real accounts get live briefs of real judgments (ADR-020). */
function DemoCaseBrief() {
  const sections: { heading: string; body: string; cite?: LegalCitation }[] = [
    {
      heading: 'Facts',
      body: 'A fictitious offer required written acceptance. The other party replied by telephone.',
    },
    {
      heading: 'Issue',
      body: 'Was a demonstration agreement formed by an oral reply?',
      cite: BRIEF_CITATIONS.p12,
    },
    {
      heading: 'Holding',
      body: 'No. Acceptance had to follow the manner the offer specified.',
      cite: BRIEF_CITATIONS.p12,
    },
    { heading: 'Note', body: 'The court did not decide consideration.', cite: BRIEF_CITATIONS.p15 },
  ];
  return (
    <Page
      title="Alpha Example v Beta Illustration (DEMO)"
      description="Case brief · Demonstration High Court (fictitious) · Brief not editorially reviewed"
    >
      <dl className="flex max-w-3xl flex-col gap-6">
        {sections.map((s) => (
          <div key={s.heading} className="flex flex-col gap-1">
            <dt className="font-serif text-lg font-semibold text-navy-900">{s.heading}</dt>
            <dd className="mt-1 flex flex-col gap-2 text-ink-900">
              <p>{s.body}</p>
              {s.cite ? <CitationLink citation={s.cite} /> : null}
            </dd>
          </div>
        ))}
      </dl>
      <PreviewAction reason="Saved notes are not yet available.">Save to my notes</PreviewAction>
    </Page>
  );
}

/** DEMO quiz for DEMO personas; real accounts get live quizzes from the library (ADR-021). */
function DemoQuizzes() {
  const quiz = useQuery({ queryKey: ['quiz'], queryFn: demoContentService.getQuiz });
  return (
    <Page
      title="Practice quizzes"
      description="Formative practice only — not an examination and not graded officially."
    >
      {quiz.isPending ? <LoadingState label="Loading quiz" /> : null}
      {quiz.isError ? (
        <ErrorState title="The quiz could not be loaded" onRetry={() => void quiz.refetch()} />
      ) : null}
      {quiz.data === null ? <EmptyState title="No quizzes available yet" /> : null}
      {quiz.data ? <QuizRunner quiz={quiz.data} /> : null}
    </Page>
  );
}

export function CaseBriefPage() {
  const tokens = useReaderTokens();
  return tokens ? <LiveCaseBriefs tokens={tokens} /> : <DemoCaseBrief />;
}

const BRIEF_RESULTS_LIMIT = 30;

/** Find a judgment, then read its cited brief (ADR-020). */
function LiveCaseBriefs({ tokens }: { tokens: IdentityClient }) {
  const cases = useCases(tokens);
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [area, setArea] = useState('');
  const searchId = useId();
  const location = useLocation();
  const selectedId = params.get('case');
  const all = useMemo(() => cases.data?.cases ?? [], [cases.data]);
  const selected = selectedId ? all.find((c) => c.versionId === selectedId) : undefined;
  const matches = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0 && !area) return [];
    return all.filter((c) => {
      if (area && !c.areas?.includes(area)) return false;
      const haystack = caseHaystack(c);
      return terms.every((t) => haystack.includes(t));
    });
  }, [all, query, area]);
  const filtering = Boolean(query.trim() || area);

  return (
    <Page
      title="Case briefs"
      description="Choose a judgment to read its brief: facts, issues, holding, reasoning and ratio, cited to the judgment."
    >
      {cases.isPending ? <LoadingState label="Loading the cases" /> : null}
      {cases.isError ? (
        <ErrorState title="The cases could not be loaded" onRetry={() => void cases.refetch()}>
          {cases.error.message}
        </ErrorState>
      ) : null}
      {selected ? (
        <section aria-labelledby="brief-case" className="flex flex-col gap-4">
          <button
            type="button"
            onClick={() => setParams({})}
            className="flex items-center gap-1 self-start text-sm font-medium text-navy-900 underline"
          >
            <ArrowLeft className="size-4" aria-hidden /> Choose another case
          </button>
          <div className="flex flex-col gap-1">
            <h2 id="brief-case" className="font-serif text-2xl font-semibold text-navy-900">
              {caseName(selected)}
            </h2>
            <p className="text-sm text-muted-600">
              {[
                selected.citation,
                COURT_NAMES[selected.court] ?? selected.court,
                selected.decisionDate,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            <Link
              to={`/library/cases/${selected.versionId}`}
              state={{ backTo: `${location.pathname}${location.search}` }}
              className="self-start text-sm font-medium text-navy-900 underline"
            >
              Open the judgment
            </Link>
          </div>
          <CaseBriefPanel key={selected.versionId} tokens={tokens} versionId={selected.versionId} />
        </section>
      ) : cases.data ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor={searchId} className="text-sm font-medium text-ink-900">
              Find a case by party, case number, citation or issue
            </label>
            <input
              id={searchId}
              type="search"
              value={query}
              placeholder="e.g. Seymour-Wilson, [1952] SLCA 5, title to land"
              onChange={(e) => setQuery(e.target.value)}
              className="h-10 rounded-md border border-line-200 bg-white px-3 text-ink-900 placeholder:text-muted-600"
            />
          </div>
          <AreaFilter cases={all} value={area} onChange={setArea} />
          <p className="text-sm text-muted-600" role="status" aria-live="polite">
            {filtering
              ? `${matches.length} matching ${matches.length === 1 ? 'case' : 'cases'}`
              : `${all.length} judgments available.`}
          </p>
          {filtering && matches.length === 0 && query.trim().length < 3 ? (
            <EmptyState title="No cases match">
              Not found within the judgments in the library. Try a party's surname or a citation.
            </EmptyState>
          ) : null}
          <ul className="flex flex-col divide-y divide-line-200">
            {matches.slice(0, BRIEF_RESULTS_LIMIT).map((c) => (
              <li key={c.versionId}>
                <button
                  type="button"
                  onClick={() => setParams({ case: c.versionId })}
                  className="flex w-full flex-col gap-0.5 px-2 py-3 text-left hover:bg-navy-50"
                >
                  <span className="font-medium text-navy-900">{caseName(c)}</span>
                  <span className="text-sm text-muted-600">
                    {[c.citation, COURT_NAMES[c.court] ?? c.court].filter(Boolean).join(' · ')}
                  </span>
                  {c.issues?.length ? (
                    <span className="text-xs text-muted-600">{c.issues.join(' · ')}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
          <CaseTextResults
            tokens={tokens}
            query={query}
            cases={all}
            area={area}
            exclude={new Set(matches.slice(0, BRIEF_RESULTS_LIMIT).map((c) => c.versionId))}
            pick={(versionId) => setParams({ case: versionId })}
          />
        </div>
      ) : null}
    </Page>
  );
}

export function QuizzesPage() {
  const tokens = useReaderTokens();
  return tokens ? <LiveQuizzes tokens={tokens} /> : <DemoQuizzes />;
}

const QUIZ_TOPICS = [
  'Title to land',
  'Bail in criminal cases',
  'Contracts for the sale of land',
  "Directors' duties",
  'Customary marriage',
  'Intestate succession',
];

/** Practice quizzes written from the Acts and judgments (ADR-021). */
function LiveQuizzes({ tokens }: { tokens: IdentityClient }) {
  const topicId = useId();
  const [topic, setTopic] = useState('');
  const location = useLocation();
  const create = useMutation({
    mutationFn: (t: string) => assistantApi.quiz(tokens, t),
  });
  const quiz = create.data;
  const start = (t: string) => {
    const chosen = t.trim();
    if (chosen.length < 3 || create.isPending) return;
    setTopic(chosen);
    create.mutate(chosen);
  };

  return (
    <Page
      title="Practice quizzes"
      description="Choose a topic: questions are written from the Acts and judgments in the library, with sources. Formative practice only."
    >
      {quiz?.outcome === 'ready' ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-serif text-xl font-semibold text-navy-900">{quiz.title}</h2>
            <Button variant="secondary" size="sm" onClick={() => create.reset()}>
              New quiz
            </Button>
          </div>
          <QuizRunner
            key={quiz.title + quiz.questions.length}
            quiz={quiz}
            resultNote="Practice result only; it is not recorded."
            renderSources={(question) =>
              question.sources.map((n) => {
                const s = quiz.sources.find((x) => x.n === n);
                if (!s) return null;
                return (
                  <Link
                    key={n}
                    to={`/library/${s.collection === 'cases' ? 'cases/' : ''}${s.versionId}?page=${s.pageStart}`}
                    state={{ backTo: `${location.pathname}${location.search}` }}
                    className="flex items-center gap-1 text-sm font-medium text-navy-900 underline"
                  >
                    <FileText className="size-4" aria-hidden /> {displayTitle(s.title)} — {s.label}
                  </Link>
                );
              })
            }
          />
        </div>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            start(topic);
          }}
        >
          <div className="flex flex-col gap-1">
            <label htmlFor={topicId} className="text-sm font-medium text-ink-900">
              Topic
            </label>
            <div className="flex gap-2">
              <input
                id={topicId}
                value={topic}
                maxLength={200}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. Adverse possession"
                className="h-10 min-w-0 flex-1 rounded-md border border-line-200 bg-white px-3 text-ink-900 placeholder:text-muted-600"
              />
              <Button type="submit" disabled={topic.trim().length < 3 || create.isPending}>
                Create quiz
              </Button>
            </div>
            <p className="text-sm text-muted-600">Each new quiz uses one of today's questions.</p>
          </div>
          <div className="flex flex-wrap gap-2" aria-label="Suggested topics">
            {QUIZ_TOPICS.map((t) => (
              <Button
                key={t}
                type="button"
                variant="secondary"
                size="sm"
                disabled={create.isPending}
                onClick={() => start(t)}
              >
                {t}
              </Button>
            ))}
          </div>
          {create.isPending ? <LoadingState label="Writing your quiz from the library" /> : null}
          {create.isError ? (
            <ErrorState title="The quiz could not be created" onRetry={() => start(topic)}>
              {create.error.message}
            </ErrorState>
          ) : null}
          {quiz ? (
            <EmptyState title="No quiz for this topic">
              Not enough was found within the Acts and judgments in the library to write questions
              on it. Try a different or broader topic.
            </EmptyState>
          ) : null}
        </form>
      )}
    </Page>
  );
}
